if (!customElements.get('fb-cart-drawer')) {
  class FluffBesenCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector('.fbc-dialog');
      this.content = this.querySelector('[data-fbc-content]');
      this.footer = this.querySelector('[data-fbc-footer]');
      this.status = this.querySelector('[data-fbc-status]');
      this.recommendations = this.querySelector('[data-fbc-recommendations]');
      this.strings = JSON.parse(this.querySelector('[data-fbc-strings]').textContent);
      const options = { signal: this.controller.signal };

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-fbp-cart-open]');
        if (!opener) return;
        event.preventDefault();
        this.open();
      }, options);
      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('fbc-open');
        this.opener?.focus();
      }, options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    async open() {
      this.opener = document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('fbc-open');
      this.querySelector('[data-fbc-close]')?.focus();
      await this.refresh();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    async refresh() {
      this.setBusy(true);
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        if (!response.ok) throw new Error(this.strings.error);
        this.render(cart);
      } catch (error) {
        this.status.textContent = error.message || this.strings.error;
      } finally {
        this.setBusy(false);
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-fbc-close]')) {
        this.close();
        return;
      }
      const control = event.target.closest('[data-fbc-quantity]');
      const recommendation = event.target.closest('[data-fbc-recommendation-add]');
      if (recommendation && !this.busy) {
        this.addRecommendation(recommendation.dataset.fbcRecommendationAdd);
        return;
      }
      if (!control || this.busy) return;
      const quantity = Number(control.dataset.fbcQuantity);
      this.change(control.dataset.fbcKey, quantity, quantity === 0);
    }

    async change(key, quantity, removing) {
      this.setBusy(true);
      this.status.textContent = '';
      try {
        const response = await fetch(`${this.dataset.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await response.json();
        if (!response.ok || cart.status) throw new Error(cart.description || this.strings.error);
        this.render(cart);
        this.status.textContent = removing ? this.strings.removed : this.strings.updated;
      } catch (error) {
        this.status.textContent = error.message || this.strings.error;
      } finally {
        this.setBusy(false);
      }
    }

    render(cart) {
      this.cart = cart;
      const count = Number(cart.item_count || 0);
      document.querySelectorAll('[data-fbp-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
      this.querySelector('#fbc-title').textContent = `${this.strings[count === 1 ? 'item' : 'items'].replace('[count]', count)}`;

      if (!count) {
        this.content.replaceChildren(this.querySelector('[data-fbc-empty-template]').content.cloneNode(true));
        this.footer.hidden = true;
        this.querySelector('[data-fbc-shipping]').hidden = true;
        return;
      }

      this.querySelector('[data-fbc-shipping]').hidden = false;
      this.footer.hidden = false;
      this.content.innerHTML = `<ul class="fbc-items">${cart.items.map((item) => this.itemMarkup(item)).join('')}</ul>`;
      this.querySelector('[data-fbc-subtotal]').textContent = this.money(cart.total_price, cart.currency);
      const savings = Number(cart.original_total_price || 0) - Number(cart.total_price || 0);
      const savingsRow = this.querySelector('[data-fbc-savings-row]');
      savingsRow.hidden = savings <= 0;
      this.querySelector('[data-fbc-savings]').textContent = savings > 0 ? `−${this.money(savings, cart.currency)}` : '';
      this.loadRecommendations();
    }

    async loadRecommendations() {
      if (this.recommendationsLoaded || !this.dataset.productId || !this.dataset.recommendationsUrl) return;
      this.recommendationsLoaded = true;
      try {
        const query = new URLSearchParams({ section_id: 'fb-cart-recommendations', product_id: this.dataset.productId, limit: '3', intent: 'related' });
        const response = await fetch(`${this.dataset.recommendationsUrl}?${query}`);
        if (!response.ok) return;
        const markup = await response.text();
        if (!markup.trim()) return;
        this.recommendations.innerHTML = markup;
        this.recommendations.hidden = !this.recommendations.textContent.trim();
      } catch (_error) {
        this.recommendations.hidden = true;
      }
    }

    async addRecommendation(variantId) {
      this.setBusy(true);
      this.status.textContent = '';
      try {
        const response = await fetch(`${this.dataset.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.strings.error);
        await this.refresh();
        this.status.textContent = this.strings.added;
      } catch (error) {
        this.status.textContent = error.message || this.strings.error;
      } finally {
        this.setBusy(false);
      }
    }

    itemMarkup(item) {
      const title = this.escape(item.product_title || item.title || '');
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${this.escape(item.variant_title)}</p>` : '';
      const imageUrl = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=180` : '';
      const image = imageUrl ? `<img src="${this.escape(imageUrl)}" alt="${title}" width="90" height="90">` : '<span class="fbc-image-placeholder"></span>';
      const original = Number(item.original_line_price || 0) > Number(item.final_line_price || 0)
        ? `<s>${this.money(item.original_line_price, this.cart?.currency)}</s>` : '';
      const key = this.escape(item.key);
      return `<li class="fbc-item">
        <a class="fbc-image" href="${this.escape(item.url || '#')}">${image}</a>
        <div class="fbc-item__details">
          <a class="fbc-item__title" href="${this.escape(item.url || '#')}">${title}</a>${variant}
          <div class="fbc-item__price">${original}<strong>${this.money(item.final_line_price, this.cart?.currency)}</strong></div>
          <div class="fbc-item__actions">
            <div class="fbc-stepper" aria-label="${this.escape(this.strings.item.replace('[count]', item.quantity))}">
              <button type="button" data-fbc-quantity="${Math.max(0, item.quantity - 1)}" data-fbc-key="${key}" aria-label="${this.escape(this.strings.decrease)}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-fbc-quantity="${item.quantity + 1}" data-fbc-key="${key}" aria-label="${this.escape(this.strings.increase)}">+</button>
            </div>
            <button class="fbc-remove" type="button" data-fbc-quantity="0" data-fbc-key="${key}" aria-label="${this.escape(this.strings.remove.replace('[product]', title))}">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6"/></svg>
              <span>${this.escape(this.strings.remove.replace('[product] ', '').replace(' [product]', ''))}</span>
            </button>
          </div>
        </div>
      </li>`;
    }

    money(cents, currency) {
      try {
        return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: currency || this.dataset.currency }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || this.dataset.currency}`;
      }
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
    }

    setBusy(busy) {
      this.busy = busy;
      this.dialog.classList.toggle('is-loading', busy);
      this.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
    }
  }
  customElements.define('fb-cart-drawer', FluffBesenCartDrawer);
}
